import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { DB, Schemas } from '@tonic/central-specialties-utils';
import {
  Box,
  Center,
  Icons,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@tonic/central-specialties-ui-themed';
import {
  Button,
  ButtonIcon,
  FlatList,
  HStack,
  Icon,
  Text,
} from '@gluestack-ui/themed';
import { RepairRequestCard } from '../../../../components/general/cards/RepairRequestCard.tsx';
import { downloadCSVFile } from '../../../../utils/downloadObjectsAsCSV.ts';
import { TimeframeData } from '../../TimeCardsPage/TimeCardsPage.tsx';
import DateRangePicker from '../../../../components/general/DateRangePicker.tsx';

interface ArchivedRepairRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  repairRequests: Schemas['RepairRequestResponse'][];
  onSelectTicket: (ticket: Schemas['RepairRequestResponse']) => void;
}

export const ArchivedRepairRequestsModal = ({
  isOpen,
  onClose,
  repairRequests,
  onSelectTicket,
}: ArchivedRepairRequestsModalProps) => {
  const [page, setPage] = useState(0);
  const [showExportSelectionModal, setShowExportSelectionModal] = useState<
    string | null
  >(null);
  const [exportLoading, setExportLoading] = useState(false);

  const downloadButtonRef = useRef<any>(null);

  useEffect(() => {
    // When the DateRangePicker is closed, restore focus to the trigger button.
    if (showExportSelectionModal === null) {
      // Delay until after re-render
      setTimeout(() => {
        try {
          downloadButtonRef.current?.focus?.();
        } catch (e) {
          // ignore
        }
      }, 0);
    }
  }, [showExportSelectionModal]);

  const itemsPerPage = 12;

  const exportArchivedRepairEntries = useCallback(
    async (dateRangeTimeFrame: TimeframeData) => {
      const { startDate, endDate } = dateRangeTimeFrame;
      setExportLoading(true);
      try {
        const options: any = { parseAs: 'text' };
        // Only send timeframe query params if both dates are provided
        if (startDate && endDate) {
          options.params = { query: { startDate, endDate } };
        }
        const response = await DB.GET(
          '/repair-requests/export-archived',
          options,
        );
        if (!response.data) {
          throw new Error('Network response was not ok');
        }
        const name =
          'Repair_archived_requests_' +
          (startDate || 'all') +
          '_to_' +
          (endDate || 'all');

        downloadCSVFile(response.data, name);
      } catch (error) {
        console.error('Download error:', error);
      } finally {
        setExportLoading(false);
      }
    },
    [],
  );

  // sort repair requests by updatedAt, with newest requests at the top
  const sortedRepairRequests = useMemo(() => {
    const sorted = [...repairRequests];
    sorted.sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });
    return sorted;
  }, [repairRequests]);

  const totalPages = useMemo(
    () => Math.ceil(sortedRepairRequests.length / itemsPerPage),
    [sortedRepairRequests.length, itemsPerPage],
  );

  const currentRepairItems = useMemo(() => {
    const startIndex = page * itemsPerPage;
    return sortedRepairRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedRepairRequests, page, itemsPerPage]);

  const handleSelectTicket = useCallback(
    (item) => {
      onSelectTicket({ ...item, status: 'Archived' });
    },
    [onSelectTicket],
  );

  // Navigation handlers
  const goToPrevPage = useCallback(() => {
    setPage((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalBackdrop />
        <ModalContent
          borderRadius={0}
          maxHeight="90%"
          maxWidth="90%"
          minWidth={500}
          aria-hidden={!!showExportSelectionModal}
        >
          <ModalHeader
            bg="$primary700"
            alignItems="center"
            justifyContent="space-between"
            py="$2"
            pl="$10"
            pr="$6"
          >
            <Text color="$white" size="lg">
              Archived Tickets
            </Text>
            <HStack space="lg" alignItems="center" gap="$6">
              <Center flexDirection="row">
                <Button
                  ref={downloadButtonRef as any}
                  size="md"
                  px="$4"
                  variant="outline"
                  onPress={() => setShowExportSelectionModal('Detailed')}
                  aria-label="Open Window to download archived tickets"
                >
                  <Icon color="$white" size="xl" as={Icons.Download} />
                </Button>
                <ModalCloseButton
                  w="$8"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Icon color="$white" size="xl" as={Icons.X} />
                </ModalCloseButton>
              </Center>
            </HStack>
          </ModalHeader>

          <ModalBody
            w="$full"
            p="$4"
            my="$2"
            flex={1}
            justifyContent="space-between"
            minHeight="$40"
          >
            <FlatList
              data={currentRepairItems}
              keyExtractor={(item) => item.id}
              numColumns={3}
              renderItem={({ item }) => (
                <Box w="$1/3" py="$1.5" px="$2.5">
                  <RepairRequestCard
                    repairRequest={item}
                    onPress={() => handleSelectTicket(item)}
                    cardProps={{ flex: 1 }}
                  />
                </Box>
              )}
              ListEmptyComponent={() => (
                <Center w="$full" h="$full">
                  <Text size="lg">No archived repair requests to show</Text>
                </Center>
              )}
              initialNumToRender={12}
            />

            {/* Pagination Controls */}
            <HStack
              space="md"
              justifyContent="center"
              alignItems="center"
              p="$4"
            >
              <Button
                variant="outline"
                isDisabled={page === 0}
                onPress={goToPrevPage}
              >
                <ButtonIcon as={Icons.ChevronLeft} />
              </Button>

              <Text>
                Page {page + 1} of {totalPages}
                {totalPages > 0
                  ? ` (${sortedRepairRequests.length} items)`
                  : ''}
              </Text>

              <Button
                variant="outline"
                isDisabled={page >= totalPages - 1}
                onPress={goToNextPage}
              >
                <ButtonIcon as={Icons.ChevronRight} />
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {showExportSelectionModal && (
        <DateRangePicker
          visible={!!showExportSelectionModal}
          loading={exportLoading}
          onClose={() => setShowExportSelectionModal(null)}
          onConfirm={exportArchivedRepairEntries}
          title={`Export Archive Tickets`}
          presetMode="archive"
        />
      )}
    </>
  );
};

export default ArchivedRepairRequestsModal;
